import type { PlayoffSeries } from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g } from "../../util/index.ts";

// Player stats split by playoff round. The regular stats rows only have one playoffs row per
// season, so each playoff game adds to a per-round tally kept on that season's playoffSeries
// object. It is saved and exported with the league, and stays small.
export const ROUND_STAT_KEYS = [
	"gp",
	"pa",
	"ab",
	"r",
	"h",
	"2b",
	"3b",
	"hr",
	"gsl",
	"rbi",
	"bb",
	"hbp",
	"sf",
	"so",
	"sb",
	"gpPit",
	"gsPit",
	"outs",
	"hPit",
	"er",
	"bbPit",
	"soPit",
	"hrPit",
	"w",
	"l",
	"sv",
] as const;

export type RoundStatsRow = {
	pid: number;
	name: string;
	pos: string;
	tid: number;
	// False only when old saved tallies lack the components needed to recover AB.
	abKnown?: boolean;
} & Record<(typeof ROUND_STAT_KEYS)[number], number>;

type PlayoffSeriesWithStats = PlayoffSeries & {
	roundStats?: Record<number, Record<number, RoundStatsRow>>;
};

// AB is derived in the baseball engine, not recorded as a raw game stat.
// Match processPlayerStats.baseball.ts, including its treatment of sacrifice flies.
export const playoffAtBats = (stats: Partial<Record<"ab" | "pa" | "bb" | "hbp" | "sf", number>>): number =>
	typeof stats.ab === "number" && Number.isFinite(stats.ab) && stats.ab > 0
		? stats.ab
		: Math.max(0, (stats.pa ?? 0) - (stats.bb ?? 0) - (stats.hbp ?? 0) - (stats.sf ?? 0));

const normalizeRecordedRow = (saved: RoundStatsRow): RoundStatsRow => {
	const known = saved.abKnown ?? (saved.pa === 0 || saved.ab > 0 ||
		(Object.hasOwn(saved, "hbp") && Object.hasOwn(saved, "sf")));
	return {
		...saved,
		...Object.fromEntries(ROUND_STAT_KEYS.map((key) => [key, saved[key] ?? 0])),
		ab: known ? playoffAtBats(saved) : 0,
		abKnown: known,
	};
};

// Called once per game day with every playoff game's players
export const recordPlayoffRoundStats = async (
	games: {
		tid: number;
		players: { id: number; name: string; pos: string; stat: Record<string, number> }[];
	}[][],
) => {
	const playoffSeries = (await idb.cache.playoffSeries.get(
		g.get("season"),
	)) as PlayoffSeriesWithStats | undefined;
	if (!playoffSeries) {
		return;
	}
	const round = playoffSeries.currentRound;
	playoffSeries.roundStats ??= {};
	const rows = (playoffSeries.roundStats[round] ??= {});
	for (const teams of games) {
		for (const t of teams) {
			for (const p of t.players) {
				if (!((p.stat.gp ?? 0) > 0)) {
					continue;
				}
				const row = (rows[p.id] ??= {
					pid: p.id,
					name: p.name,
					pos: p.pos,
					tid: t.tid,
					...(Object.fromEntries(ROUND_STAT_KEYS.map((key) => [key, 0])) as Record<
						(typeof ROUND_STAT_KEYS)[number],
						number
					>),
				});
				// Old rows can already have missing AB. Do not pretend one new game
				// repairs earlier games, or add undefined counters after an upgrade.
				row.abKnown ??= row.pa === 0 || (row.ab ?? 0) > 0;
				for (const key of ROUND_STAT_KEYS) {
					row[key] = (row[key] ?? 0) + (key === "ab" ? playoffAtBats(p.stat) : p.stat[key] ?? 0);
				}
			}
		}
	}
	await idb.cache.playoffSeries.put(playoffSeries);
};

// Stats for one round of one season: recorded tallies when they exist, otherwise rebuilt
// from the box scores still saved, otherwise nothing (older box scores are deleted per the
// league's "save old box scores" setting).
export const getPlayoffRoundStats = async (
	playoffSeries: PlayoffSeriesWithStats,
	round: number,
): Promise<{ rows: RoundStatsRow[]; source: "recorded" | "boxScores" | "missing" }> => {
	const recorded = playoffSeries.roundStats?.[round];
	const savedRows = recorded && Object.keys(recorded).length > 0
		? Object.values(recorded).map(normalizeRecordedRow)
		: undefined;
	if (savedRows?.every((row) => row.abKnown)) {
		return { rows: savedRows, source: "recorded" };
	}
	const gids = (playoffSeries.series[round] ?? []).flatMap((series) => series.gids ?? []);
	if (gids.length === 0) {
		return savedRows ? { rows: savedRows, source: "recorded" } : { rows: [], source: "missing" };
	}
	const games = await idb.getCopies.games({ gids }, "noCopyCache");
	if (games.length !== gids.length) {
		return savedRows ? { rows: savedRows, source: "recorded" } : { rows: [], source: "missing" };
	}
	const rows = new Map<number, RoundStatsRow>();
	for (const game of games) {
		for (const t of game.teams) {
			for (const p of t.players as any[]) {
				if (!(p.gp > 0)) {
					continue;
				}
				let row = rows.get(p.pid);
				if (!row) {
					row = {
						pid: p.pid,
						name: p.name,
						pos: p.pos,
						tid: t.tid,
						...(Object.fromEntries(ROUND_STAT_KEYS.map((key) => [key, 0])) as Record<
							(typeof ROUND_STAT_KEYS)[number],
							number
						>),
					};
					rows.set(p.pid, row);
				}
				row.abKnown = true;
				for (const key of ROUND_STAT_KEYS) {
					row[key] += key === "ab" ? playoffAtBats(p) : p[key] ?? 0;
				}
			}
		}
	}
	if (savedRows) {
		// Read-only repair for old saves; the original records are untouched.
		return { rows: savedRows.map((row) => {
			const rebuilt = rows.get(row.pid);
			return !row.abKnown && rebuilt && rebuilt.pa === row.pa && rebuilt.gp === row.gp
				? { ...row, ab: rebuilt.ab, abKnown: true }
				: row;
		}), source: "recorded" };
	}
	return { rows: [...rows.values()], source: "boxScores" };
};
